"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-spring-manufacturing";
const IMAGE_BASE = "/image/new-version/more/SpringManufacturing/MaterialSelection";

const MATERIALS = [
	{
		keyPrefix: "materialSelectionStainlessSteelSeries",
		images: [
			"stainless-steel-series-1.webp",
			"stainless-steel-series-2.webp",
			"stainless-steel-series-3.webp",
			"stainless-steel-series-4.webp",
			"stainless-steel-series-5.webp",
		],
		entryCount: 4,
	},
	{
		keyPrefix: "materialSelectionCarbonSteelPianoWire",
		images: [
			"carbon-steel-piano-wire-1.webp",
			"carbon-steel-piano-wire-2.webp",
			"carbon-steel-piano-wire-3.webp",
			"carbon-steel-piano-wire-4.webp",
		],
		entryCount: 4,
	},
	{
		keyPrefix: "materialSelectionCopperAlloyMaterials",
		images: [
			"copper-alloy-materials-1.webp",
			"copper-alloy-materials-2.webp",
			"copper-alloy-materials-3.webp",
			"copper-alloy-materials-4.webp",
		],
		entryCount: 4,
	},
	{
		keyPrefix: "materialSelectionSpecialtyAlloyMaterials",
		images: [
			"specialty-alloy-materials-1.webp",
			"specialty-alloy-materials-2.webp",
			"specialty-alloy-materials-3.webp",
			"specialty-alloy-materials-4.webp",
			"specialty-alloy-materials-5.webp",
		],
		entryCount: 3,
	},
] as const;

export default function SpringManufacturingMaterialSelection() {
	const t = useTranslations(NAMESPACE);
	const [activeIndex, setActiveIndex] = useState(0);
	const [activeImageIndex, setActiveImageIndex] = useState(0);

	const activeMaterial = MATERIALS[activeIndex];
	const activeImageAlt = t(`${activeMaterial.keyPrefix}ImageAlt`);

	const handleMaterialChange = (index: number) => {
		setActiveIndex(index);
		setActiveImageIndex(0);
	};

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("materialSelectionTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("materialSelectionDesc")}</p>
				</header>

				<div className="grid overflow-hidden bg-white shadow-sm lg:grid-cols-[280px_1fr]">
					<aside className="bg-[#f1f1f1] p-3 sm:p-4">
						<h3 className="mb-2 inline-flex rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-black">
							{t("materialSelectionMetalsTitle")}
						</h3>
						<div className="flex gap-1 overflow-x-auto pb-1 lg:max-h-[420px] lg:flex-col lg:overflow-y-auto lg:pb-0">
							{MATERIALS.map((material, index) => (
								<button
									key={material.keyPrefix}
									type="button"
									onClick={() => handleMaterialChange(index)}
									className={`shrink-0 px-4 py-3 text-left text-sm font-semibold transition-colors lg:w-full ${
										activeIndex === index
											? "bg-white text-themePrimary400"
											: "text-gray-700 hover:bg-white hover:text-themePrimary400"
									}`}
								>
									{t(`${material.keyPrefix}Title`)}
								</button>
							))}
						</div>
					</aside>

					<div className="min-w-0 bg-white">
						<div className="border-b border-[#b0b0b0] p-5 sm:p-6">
							<span className="inline-flex rounded bg-themePrimary400 px-4 py-2 text-sm font-bold text-white">
								{t(`${activeMaterial.keyPrefix}Title`)}
							</span>
						</div>

						<div className="grid lg:grid-cols-2">
							<div className="border-b border-[#b0b0b0] p-5 sm:p-6 lg:border-b-0 lg:border-r">
								<div className="relative mb-4 aspect-[4/2.35] w-full overflow-hidden bg-[#bfbfbf]">
									<Image
										key={`${activeMaterial.keyPrefix}-${activeImageIndex}`}
										src={`${IMAGE_BASE}/${activeMaterial.images[activeImageIndex]}`}
										alt={activeImageAlt}
										fill
										className="object-contain p-4"
										sizes="(max-width: 1024px) 100vw, 420px"
									/>
								</div>
								<div className="grid grid-cols-3 gap-3">
									{activeMaterial.images.map((image, index) => (
										<button
											key={image}
											type="button"
											onClick={() => setActiveImageIndex(index)}
											className={`relative aspect-[4/3] overflow-hidden border-2 bg-[#f5f7fa] transition-colors ${
												activeImageIndex === index
													? "border-themePrimary400"
													: "border-gray-200 hover:border-gray-400"
											}`}
											aria-label={t("materialSelectionThumbnailLabel", {
												material: t(`${activeMaterial.keyPrefix}Title`),
												index: index + 1,
											})}
										>
											<Image
												src={`${IMAGE_BASE}/${image}`}
												alt={`${activeImageAlt} ${index + 1}`}
												fill
												className="object-contain p-2"
												sizes="(max-width: 640px) 30vw, 120px"
											/>
										</button>
									))}
								</div>
							</div>

							<div className="p-5 sm:p-6 lg:p-8">
								<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(`${activeMaterial.keyPrefix}Title`)}
								</h3>
								<p className="mb-6 text-sm leading-relaxed text-black md:text-base">
									{t(`${activeMaterial.keyPrefix}Desc`)}
								</p>

								<div className="space-y-4">
									{Array.from({ length: activeMaterial.entryCount }, (_, index) => {
										const entryPrefix = `${activeMaterial.keyPrefix}Entry${index + 1}`;

										return (
											<div
												key={entryPrefix}
												className="grid gap-1 text-sm text-black sm:grid-cols-[170px_1fr]"
											>
												<span className="font-semibold">{t(`${entryPrefix}Name`)}</span>
												<span>{t(`${entryPrefix}Use`)}</span>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="mx-auto mt-8 max-w-5xl border border-[#878787] bg-white p-5 shadow-sm sm:p-6">
					<div className="flex items-center gap-4">
						<span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f0f8e8] text-themePrimary400">
							<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 3a6 6 0 0 0-3.6 10.8c.5.38.85.96.85 1.58V16.5h5.5v-1.12c0-.62.35-1.2.85-1.58A6 6 0 0 0 12 3Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9.75 19h4.5M10.5 21h3"
								/>
							</svg>
						</span>
						<div>
							<h3 className="mb-2 text-base font-bold text-black sm:text-lg">
								{t("materialSelectionRecommendationTitle")}
							</h3>
							<p className="text-sm leading-relaxed text-black">{t("materialSelectionRecommendationDesc")}</p>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
